import { useCallback, useEffect, useState } from 'react';
import type { Order, OrderItem, OrderStatus } from '../../lib/database.types';
import {
  loadOrders,
  subscribeOrders,
  statusLabel,
  paymentStatusLabel,
  sortByNewest,
  updateOrderStatus,
  cancelOrder,
  deleteOrder,
  NEXT_STATUS,
  isAwaitingOnlinePayment,
} from '../../lib/orders';
import { formatPrice } from '../../lib/cart';
import ConfirmDelete from './ConfirmDelete';
import { playOrderAlert, stopOrderAlert } from '../../lib/orderSiren';

/**
 * Vue Commandes du back-office (parité legacy) : bannière « à accepter » + son,
 * onglets (À traiter / Aujourd'hui / Toutes), recherche, export CSV, et cartes
 * commande avec Accepter / Refuser puis progression cuisine. Temps réel via Supabase.
 */
type TabId = 'todo' | 'today' | 'all';
const TODO_STATUSES: OrderStatus[] = ['recue', 'acceptee', 'en_cuisine', 'prete'];

function timeAgo(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  return `il y a ${h} h`;
}
function isToday(iso: string): boolean {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}
export default function OrdersAdmin({
  tenantId,
  currency = 'EUR',
  tenantName,
}: {
  tenantId: string;
  currency?: 'EUR' | 'CHF';
  tenantName?: string;
}) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [items, setItems] = useState<Record<string, OrderItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>('todo');
  const [search, setSearch] = useState('');
  const [soundOn, setSoundOn] = useState(true);
  // Sélection multiple + actions groupées (parité legacy : annuler / supprimer).
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const toggleSel = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const clearSel = () => setSelected(new Set());

  const refresh = useCallback(async () => {
    const { orders: list, itemsByOrder } = await loadOrders(tenantId);
    setOrders(sortByNewest(list));
    setItems(itemsByOrder);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    refresh();
    const sub = subscribeOrders(tenantId, {
      onInsert: () => !cancelled && refresh(),
      onUpdate: () => !cancelled && refresh(),
    });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [tenantId, refresh]);

  // « En attente d'acceptation » = reçues ET (payées ou sur place). Les commandes en ligne
  // non payées ne comptent pas (pas de sonnerie tant que Stripe n'a pas confirmé).
  const pending = orders.filter((o) => o.status === 'recue' && !isAwaitingOnlinePayment(o));
  // ALARME RÉPÉTÉE (parité comptoir, MÊME sirène forte que la tablette) : tant qu'une
  // commande « reçue » n'est ni acceptée ni refusée, on sonne 5 s puis toutes les 10 s.
  useEffect(() => {
    if (!soundOn || pending.length === 0) {
      stopOrderAlert();
      return;
    }
    playOrderAlert({ duration: 5000 });
    const id = setInterval(() => playOrderAlert({ duration: 5000 }), 10000);
    return () => clearInterval(id);
  }, [pending.length, soundOn]);

  const q = search.trim().toLowerCase();
  const filtered = orders.filter((o) => {
    // RÈGLE : une commande en ligne NON payée (client parti avant de payer) ne figure PAS
    // dans la file de travail (À traiter / Aujourd'hui) — visible seulement dans « Toutes ».
    if (tab !== 'all' && isAwaitingOnlinePayment(o)) return false;
    if (tab === 'todo' && !TODO_STATUSES.includes(o.status)) return false;
    if (tab === 'today' && !isToday(o.created_at)) return false;
    if (q) {
      const hay = `${o.order_number ?? ''} ${o.customer_name ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const exportCsv = () => {
    const head = ['N°', 'Date', 'Client', 'Statut', 'Mode', 'Total'];
    const rows = filtered.map((o) => [
      o.order_number ?? o.id.slice(0, 6),
      new Date(o.created_at).toLocaleString('fr-FR'),
      o.customer_name ?? '',
      o.status,
      o.pickup_mode === 'delivery' ? 'Livraison' : 'Retrait',
      (o.total_cents / 100).toFixed(2),
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `commandes-${tenantName ?? tenantId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exitSelect = () => {
    setSelectMode(false);
    clearSel();
  };
  const bulkCancel = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    for (const id of selected) await cancelOrder(id);
    setBulkBusy(false);
    exitSelect();
    refresh();
  };
  // Suppression définitive = confirmée par le MOT DE PASSE admin (parité legacy, modale ConfirmDelete).
  const doDelete = async () => {
    setConfirmDel(false);
    if (selected.size === 0) return;
    setBulkBusy(true);
    let firstError: string | undefined;
    for (const id of selected) {
      const res = await deleteOrder(id);
      if (!res.ok && !firstError) firstError = res.error;
    }
    setBulkBusy(false);
    exitSelect();
    refresh();
    if (firstError) window.alert(`Suppression impossible : ${firstError}. Contactez Lil & Co (droits base à ajuster).`);
  };

  if (loading) return <p className="opacity-70">Chargement des commandes…</p>;

  const tabs: { id: TabId; label: string }[] = [
    { id: 'todo', label: `À traiter (${orders.filter((o) => TODO_STATUSES.includes(o.status) && !isAwaitingOnlinePayment(o)).length})` },
    { id: 'today', label: 'Aujourd’hui' },
    { id: 'all', label: 'Toutes' },
  ];

  return (
    <div className="flex flex-col gap-4">
      {pending.length > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-2xl bg-amber-400/90 px-5 py-4 text-amber-950">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🔔</span>
            <div>
              <div className="font-extrabold">
                {pending.length} commande{pending.length > 1 ? 's' : ''} en attente d'acceptation
              </div>
              <div className="text-sm opacity-80">À accepter ou refuser dès que possible.</div>
            </div>
          </div>
          <button
            onClick={() =>
              setSoundOn((s) => {
                const n = !s;
                if (n) playOrderAlert({ duration: 1500 });
                else stopOrderAlert();
                return n;
              })
            }
            className="rounded-xl bg-amber-950/10 px-4 py-2 text-sm font-bold hover:bg-amber-950/20"
          >
            {soundOn ? '🔕 Couper le son' : '🔔 Activer le son'}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                'rounded-lg px-4 py-2 text-sm font-bold transition ' +
                (tab === t.id ? 'bg-white shadow-sm' : 'text-slate-500 hover:text-slate-700')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="N° commande, client…"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none"
          />
          <button
            onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
            className={
              'rounded-lg border px-3 py-2 text-sm font-semibold ' +
              (selectMode ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-200 hover:bg-slate-50')
            }
          >
            {selectMode ? '✕ Annuler la sélection' : '☑ Sélectionner'}
          </button>
          <button
            onClick={() => playOrderAlert({ duration: 1500 })}
            title="Vérifie et débloque le son (à faire une fois en début de service)"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            🔊 Test son
          </button>
          <button
            onClick={exportCsv}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <span className="text-sm font-bold text-amber-900">
            {selected.size} commande{selected.size > 1 ? 's' : ''} sélectionnée{selected.size > 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setSelected(new Set(filtered.map((o) => o.id)))}
            className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100"
          >
            ☑ Tout sélectionner ({filtered.length})
          </button>
          <button
            disabled={bulkBusy || selected.size === 0}
            onClick={bulkCancel}
            className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-40"
          >
            ✕ Annuler (garde la trace)
          </button>
          <button
            disabled={bulkBusy || selected.size === 0}
            onClick={() => setConfirmDel(true)}
            className="rounded-lg bg-red-600 px-3 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-40"
          >
            🗑 Supprimer définitivement
          </button>
          {selected.size > 0 && (
            <button onClick={clearSel} className="text-sm font-semibold text-slate-500 hover:text-slate-700">
              Tout désélectionner
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="py-10 text-center opacity-60">Aucune commande dans cette vue.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {filtered.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              items={items[o.id] ?? []}
              currency={currency}
              selectMode={selectMode}
              selected={selected.has(o.id)}
              onToggleSelect={() => toggleSel(o.id)}
            />
          ))}
        </ul>
      )}

      <ConfirmDelete
        open={confirmDel}
        label={`${selected.size} commande${selected.size > 1 ? 's' : ''}`}
        onCancel={() => setConfirmDel(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}

function OrderCard({
  order: o,
  items,
  currency,
  selectMode = false,
  selected = false,
  onToggleSelect,
}: {
  order: Order;
  items: OrderItem[];
  currency: 'EUR' | 'CHF';
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const act = async (status: OrderStatus, reason?: string) => {
    setBusy(true);
    await updateOrderStatus(o.id, status, reason);
    setBusy(false);
  };
  const next = NEXT_STATUS[o.status];

  return (
    <li className={`rounded-2xl border bg-white p-4 ${selected ? 'border-amber-400 ring-2 ring-amber-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        {selectMode && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Sélectionner la commande ${o.order_number ?? o.id.slice(0, 6)}`}
            className="mt-1.5 h-5 w-5 flex-shrink-0 accent-amber-500"
          />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold">
              {statusLabel(o.status)}
            </span>
            <span className="font-bold">#{o.order_number ?? o.id.slice(0, 6)}</span>
            <span className="text-xs text-slate-400">{timeAgo(o.created_at)}</span>
          </div>
          <div className="mt-1 font-semibold">{o.customer_name ?? 'Client'}</div>
          <div className="text-sm text-slate-500">
            {o.pickup_mode === 'delivery' ? 'Livraison' : 'Retrait'}
            {o.pickup_time ? ` · prévu ${o.pickup_time.slice(0, 5)}` : ''} · {paymentStatusLabel(o.payment_status)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-extrabold">{formatPrice(o.total_cents, currency)}</div>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
          {items.map((it) => (
            <div key={it.id} className="flex justify-between">
              <span>×{it.quantity} {it.name}</span>
              <span className="opacity-60">{formatPrice(it.unit_price_cents * it.quantity, currency)}</span>
            </div>
          ))}
        </div>
      )}

      {o.customer_note && (
        <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">📝 {o.customer_note}</div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {isAwaitingOnlinePayment(o) ? (
          <div className="w-full rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-2.5 text-center font-bold text-amber-800">
            ⏳ En attente du paiement en ligne du client — ne pas préparer
          </div>
        ) : (
          <>
            {o.status === 'recue' && (
              <>
                <button
                  disabled={busy}
                  onClick={() => act('refusee')}
                  className="flex-1 rounded-xl border-2 border-red-200 px-4 py-2.5 font-bold text-red-600 hover:bg-red-50 disabled:opacity-40"
                >
                  ✕ Refuser
                </button>
                <button
                  disabled={busy}
                  onClick={() => act('acceptee')}
                  className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 font-bold text-white hover:bg-emerald-700 disabled:opacity-40"
                >
                  ✓ Accepter
                </button>
              </>
            )}
            {next && (
              <button
                disabled={busy}
                onClick={() => act(next.status)}
                className="flex-1 rounded-xl px-4 py-2.5 font-bold text-white disabled:opacity-40"
                style={{ background: 'var(--brand-primary)' }}
              >
                {next.label} →
              </button>
            )}
          </>
        )}
      </div>
    </li>
  );
}
